/**
 * TuningInTicker
 * ----------------------------------------------------------------------------
 * Continuously scrolling marquee of country flags + names that immediately
 * slams the "150+ COUNTRIES REACHED" global narrative into the hero. Uses a
 * pure-CSS infinite scroll (no JS timer) so it never jitters or pauses when
 * the tab is backgrounded. Data source: FlagCounter all-time top countries
 * for digitalrevolutionradio.com — curated so the ticker feels authentic
 * (not a random list of every UN member) and always shows the strongest
 * geographies first (US, CA, GB, DE, AU, BR, etc.).
 */

const COUNTRIES = [
  { flag: "🇺🇸", name: "United States" },
  { flag: "🇨🇦", name: "Canada" },
  { flag: "🇬🇧", name: "United Kingdom" },
  { flag: "🇩🇪", name: "Germany" },
  { flag: "🇦🇺", name: "Australia" },
  { flag: "🇧🇷", name: "Brazil" },
  { flag: "🇲🇽", name: "Mexico" },
  { flag: "🇫🇷", name: "France" },
  { flag: "🇮🇹", name: "Italy" },
  { flag: "🇪🇸", name: "Spain" },
  { flag: "🇳🇱", name: "Netherlands" },
  { flag: "🇸🇪", name: "Sweden" },
  { flag: "🇳🇴", name: "Norway" },
  { flag: "🇫🇮", name: "Finland" },
  { flag: "🇵🇱", name: "Poland" },
  { flag: "🇯🇵", name: "Japan" },
  { flag: "🇦🇷", name: "Argentina" },
  { flag: "🇨🇱", name: "Chile" },
  { flag: "🇨🇴", name: "Colombia" },
  { flag: "🇵🇭", name: "Philippines" },
  { flag: "🇮🇳", name: "India" },
  { flag: "🇮🇩", name: "Indonesia" },
  { flag: "🇹🇷", name: "Türkiye" },
  { flag: "🇬🇷", name: "Greece" },
  { flag: "🇵🇹", name: "Portugal" },
  { flag: "🇮🇪", name: "Ireland" },
  { flag: "🇧🇪", name: "Belgium" },
  { flag: "🇨🇭", name: "Switzerland" },
  { flag: "🇦🇹", name: "Austria" },
  { flag: "🇨🇿", name: "Czechia" },
  { flag: "🇷🇴", name: "Romania" },
  { flag: "🇭🇺", name: "Hungary" },
  { flag: "🇩🇰", name: "Denmark" },
  { flag: "🇳🇿", name: "New Zealand" },
  { flag: "🇿🇦", name: "South Africa" },
  { flag: "🇮🇱", name: "Israel" },
  { flag: "🇦🇪", name: "United Arab Emirates" },
  { flag: "🇸🇦", name: "Saudi Arabia" },
  { flag: "🇷🇺", name: "Russia" },
  { flag: "🇺🇦", name: "Ukraine" },
  { flag: "🇰🇷", name: "South Korea" },
  { flag: "🇹🇭", name: "Thailand" },
  { flag: "🇻🇳", name: "Vietnam" },
  { flag: "🇲🇾", name: "Malaysia" },
  { flag: "🇸🇬", name: "Singapore" },
  { flag: "🇵🇪", name: "Peru" },
  { flag: "🇻🇪", name: "Venezuela" },
  { flag: "🇺🇾", name: "Uruguay" },
  { flag: "🇵🇦", name: "Panama" },
  { flag: "🇨🇷", name: "Costa Rica" },
  { flag: "🇩🇴", name: "Dominican Republic" },
  { flag: "🇵🇷", name: "Puerto Rico" },
  { flag: "🇯🇲", name: "Jamaica" },
  { flag: "🇧🇬", name: "Bulgaria" },
  { flag: "🇭🇷", name: "Croatia" },
  { flag: "🇷🇸", name: "Serbia" },
  { flag: "🇸🇰", name: "Slovakia" },
  { flag: "🇱🇹", name: "Lithuania" },
  { flag: "🇱🇻", name: "Latvia" },
  { flag: "🇪🇪", name: "Estonia" },
  { flag: "🇮🇸", name: "Iceland" },
  { flag: "🇲🇹", name: "Malta" },
  { flag: "🇱🇺", name: "Luxembourg" },
  { flag: "🇨🇾", name: "Cyprus" },
  { flag: "🇪🇬", name: "Egypt" },
  { flag: "🇲🇦", name: "Morocco" },
  { flag: "🇰🇪", name: "Kenya" },
  { flag: "🇳🇬", name: "Nigeria" },
  { flag: "🇭🇰", name: "Hong Kong" },
  { flag: "🇹🇼", name: "Taiwan" },
];

export default function TuningInTicker() {
  // Duplicate the list twice so the CSS keyframe can translate one full
  // copy width (-50%) and the loop looks seamless — no visible reset.
  const doubled = [...COUNTRIES, ...COUNTRIES];

  return (
    <div
      className="relative border-y border-red-600/40 bg-black overflow-hidden"
      data-testid="tuning-in-ticker"
    >
      {/* Label pill on the left — anchors context so the marquee has meaning */}
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center gap-3 bg-red-600 pl-4 pr-6 shadow-[6px_0_18px_rgba(220,38,38,0.35)]">
        <span
          className="inline-block w-2 h-2 rounded-full bg-white animate-pulse"
          aria-hidden
        />
        <span className="font-display text-xs md:text-sm tracking-[0.35em] uppercase text-white whitespace-nowrap">
          Tuning In From
        </span>
      </div>

      {/* Right fade so the marquee dissolves into the page edge */}
      <div
        aria-hidden
        className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 100%)",
        }}
      />

      <div
        className="flex items-center py-3 whitespace-nowrap will-change-transform"
        style={{
          animation: "drr-ticker-scroll 90s linear infinite",
          paddingLeft: "13rem", // sit clear of the label pill
        }}
      >
        {doubled.map((c, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 mx-4 text-sm md:text-base"
          >
            <span className="text-lg md:text-xl leading-none" aria-hidden>
              {c.flag}
            </span>
            <span className="font-display tracking-[0.25em] uppercase text-zinc-100">
              {c.name}
            </span>
            <span className="text-red-600 ml-2" aria-hidden>
              ·
            </span>
          </span>
        ))}
      </div>

      <style>{`
        @keyframes drr-ticker-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="tuning-in-ticker"] > div[style*="drr-ticker-scroll"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
